import { useState } from 'react'
import type { ProcessListItem } from '../../occurrences/types/occurrences'

/** Mesmo limite do backend (`MAX_RELATED_PROCESSES`, create-system-issue.dto.ts). */
export const MAX_RELATED_PROCESSES = 10

export type ProcessAnswer = 'yes' | 'no' | null

interface UseIssueProcessLinkOptions {
  /** Processo da página atual (widget aberto no processo, aba HUB). */
  contextProcessId: number | null
  /** Aba HUB do processo: resposta "sim" fixa e o processo dela não sai. */
  locked: boolean
  /** Quem chegou já dizendo que é do processo (modal de Ocorrências). */
  initialYes: boolean
  /** Sem acesso a Ocorrências não há busca de processo. */
  canSearch: boolean
}

/**
 * Pergunta obrigatória do novo chamado: "tem relação com um ou mais
 * processos?". Muita gente esquecia de vincular — com "sim", o envio exige ao
 * menos um processo: o da página atual entra sozinho (dá para tirar, menos na
 * aba HUB) e os demais vêm da busca. Sem processo na página e sem busca, não
 * há o que vincular e a pergunta não aparece.
 */
export function useIssueProcessLink({
  contextProcessId,
  locked,
  initialYes,
  canSearch,
}: UseIssueProcessLinkOptions) {
  const startsYes = contextProcessId != null && (locked || initialYes)
  const [answer, setAnswer] = useState<ProcessAnswer>(startsYes ? 'yes' : null)
  const [contextRemoved, setContextRemoved] = useState(false)
  const [picked, setPicked] = useState<ProcessListItem[]>([])

  const asks = contextProcessId != null || canSearch
  const isLocked = locked && contextProcessId != null
  const current: ProcessAnswer = isLocked ? 'yes' : answer
  const contextIds =
    contextProcessId != null && (isLocked || !contextRemoved) ? [contextProcessId] : []
  const withContext = contextIds.length > 0
  const linkedIds =
    current === 'yes' ? [...new Set([...contextIds, ...picked.map((p) => p.id)])] : []

  return {
    /** A pergunta aparece no formulário. */
    asks,
    answer: current,
    locked: isLocked,
    setAnswer: (value: Exclude<ProcessAnswer, null>) => {
      if (!isLocked) setAnswer(value)
    },
    /** O processo da página está na lista. */
    withContext,
    removeContext: () => setContextRemoved(true),
    /** Escolhidos na busca, na ordem em que entraram. */
    picked,
    add: (process: ProcessListItem) =>
      setPicked((list) =>
        list.some((p) => p.id === process.id) ? list : [...list, process],
      ),
    remove: (id: number) =>
      setPicked((list) => list.filter((p) => p.id !== id)),
    /** O que vai no envio (`related_process_ids`). */
    linkedIds,
    /** Pode enviar: respondeu "não", ou "sim" com ao menos um processo. */
    valid: !asks || current === 'no' || linkedIds.length > 0,
    canAddMore: canSearch && linkedIds.length < MAX_RELATED_PROCESSES,
    /** Empresa do 1º processo da busca — o seletor de empresa acompanha. */
    firstPickedCompanyId: picked[0]?.company_id ?? null,
    reset: () => {
      setAnswer(startsYes ? 'yes' : null)
      setContextRemoved(false)
      setPicked([])
    },
  }
}

export type IssueProcessLink = ReturnType<typeof useIssueProcessLink>
